import React, { useState, useEffect } from 'react';
import { fetchTableData } from './services/api';
import DataTable from './components/DataTable';
import DataGraph from './components/DataGraph';
import FixedRangeView from './components/FixedRangeView';
import DateRangeView from './components/DateRangeView';
import CapacityComparison from './components/CapacityComparison';
import { 
  Container, 
  Typography, 
  LinearProgress, 
  Box, 
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Button,
  Stack
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart'; // Use for capacity comparison icon
import * as XLSX from 'xlsx';
import './App.css';

function App() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTable, setActiveTable] = useState('bhabani');
  const [activeTab, setActiveTab] = useState(0);
  const [fixedTabView, setFixedTabView] = useState('graph'); // 'graph' or 'capacity'

  // Polling for active table (auto-refresh)
  useEffect(() => {
    let intervalId;
    const fetchActiveTable = async () => {
      try {
        const tableData = await fetchTableData(activeTable);
        setData(prev => ({ ...prev, [activeTable]: tableData }));
      } catch (err) {
        // Optionally handle error
      }
    };
    // Initial fetch
    fetchActiveTable();
    // Poll every 30 seconds
    intervalId = setInterval(fetchActiveTable, 30000);
    return () => clearInterval(intervalId);
  }, [activeTable]);

  useEffect(() => {
    const tables = ['bhabani', 'dr-reddy', 'dr-reddy-sez', 'nj-food', 'pranjul', 'vinayak', 'enrich', 'fateh', 'garg', 'girija', 'kandhari', 'nectafresh-11t', 'nectafresh-25t', 'suhans', 'synthimed-labs', 'thakar-das', 'torrent', 'uniparts', 'vibroacoustic', 'mohali', 'laxmi-organics', 'ks-textile', 'clear-choice', 'baddi', 'parle', 'zydus1', 'dhej'
    ];
    
    const fetchAllData = async () => {
      try {
        const results = {};
        for (const table of tables) {
          results[table] = await fetchTableData(table);
        }
        setData(results);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch data');
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const tables = [
    'bhabani', 'dr-reddy', 'dr-reddy-sez', 'nj-food', 'pranjul', 
    'vinayak', 'enrich', 'fateh', 'garg', 'girija', 'kandhari', 
    'nectafresh-11t', 'nectafresh-25t', 'suhans', 'synthimed-labs', 
    'thakar-das', 'torrent', 'uniparts', 'vibroacoustic', 'mohali',
    'laxmi-organics', 'ks-textile', 'clear-choice', 'baddi', 'parle',
    'zydus1', 'dhej', 'gkn', 'ajanta', 'warangal' // Added WARANGAL to site list
  ];

  // Helper to get display name for table
  const getDisplayName = (name) => {
    if (!name) return '';
    if (name.toLowerCase() === 'dr-reddy') return 'DR REDDY 4T';
    if (name.toLowerCase() === 'dr-reddy-sez') return 'DR REDDY SEZ';
    if (name.toLowerCase() === 'laxmi-organics') return 'LAXMI ORGANICS 5T';
    if (name.toLowerCase() === 'baddi') return 'BADDI';
    if (name.toLowerCase() === 'parle') return 'Parle Biscuit';
    if (name.toLowerCase() === 'zydus1') return 'Zydus Healthcare';
    if (name.toLowerCase() === 'dhej') return 'Laxmi Organics - DHEJ';
    if (name.toLowerCase() === 'gkn') return 'GKN'; // Added GKN display name
    if (name.toLowerCase() === 'ajanta') return 'AJANTA'; // Added AJANTA display name
    if (name.toLowerCase() === 'warangal') return 'WARANGAL'; // Added WARANGAL display name
    // ...add more mappings as needed...
    return name.replace(/-/g, ' ').toUpperCase();
  };

  const handleTableChange = (event) => {
    setActiveTable(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Export all sites data as Excel
  const handleExportAllSites = () => {
    // Helper to get export keys for each site
    const getExportKeys = (rows, site) => {
      if (!Array.isArray(rows) || rows.length === 0) return {};
      const first = rows[0];
      const siteName = site.toLowerCase();
      if (siteName === 'laxmi-organics') {
        return {
          feed: 'FEED_FLOW_TOT_CUR_1H_LAXMI3',
          cond: 'COND_FLOW_TOT_CUR_1H_LAXMI3',
          power: 'EM_POWER_TOT_CUR_1H_LAXMI3'
        };
      }
      if (siteName === 'ks-textile') {
        return {
          feed: 'FEED_FLOW_PRE_1H_TOT_KS',
          cond: 'COND_FLOW_PRE_1H_TOT_KS',
          power: 'POWER_PRE_1H_TOT_KS'
        };
      }
      if (siteName === 'clear-choice') {
        return {
          feed: 'FEED_FLOW_TOT_PRE_1H_CCHOICE',
          cond: 'COND_FLOW_TOT_PRE_1H_CCHOICE',
          power: 'EM_POWER_TOT_PRE_1H_CCHOICE'
        };
      }
      if (siteName === 'baddi') {
        return {
          feed: null,
          cond: null,
          power: 'Baddi_Power_PRE_1h_TOT'
        };
      }
      // Mohali
      if (siteName === 'mohali') {
        return {
          feed: null,
          cond: null,
          power: 'EM_POWER_PRE_1HR'
        };
      }
      if (siteName === 'zydus1') {
        return {
          feed: 'FEED_FLOW_PRE_1H_ZYDUS',
          cond: 'COND_FLOW_PRE_1H_ZYDUS',
          power: 'POWER_PRE_1H_ZYDUS'
        };
      }
      if (siteName === 'dhej') {
        return {
          feed: 'FEED_FLOW_TOT_PRE_1H_DHEJ',
          cond: 'COND_FLOW_TOT_PRE_1H_DHEJ',
          power: 'EM_POWER_TOT_PRE_1H_DHEJ'
        };
      }
      if (siteName === 'gkn') {
        return {
          feed: 'FEED_FLOW_PRE_1H_GKN',
          cond: 'COND_FLOW_PRE_1H_GKN',
          power: 'POWER_PRE_1H_GKN'
        };
      }
      if (siteName === 'ajanta') {
        return {
          feed: 'FEED_TOT_PRE_1H_AJANTA',
          cond: 'COND_TOT_PRE_1H_AJANTA',
          power: 'POWER_TOT_PRE_1H_AJANTA'
        };
      }
      if (siteName === 'warangal') {
        return {
          feed: 'FEED_TOT_PRE_1H_WARANGAL',
          cond: 'COND_TOT_PRE_1H_WARANGAL',
          power: 'POWER_TOT_PRE_1H_WARANGAL'
        };
      }
      // Generic
      const keys = Object.keys(first);
      return {
        feed: keys.find(k => k.toUpperCase().includes('FEED') && !k.toUpperCase().includes('CAPACITY')),
        cond: keys.find(k => k.toUpperCase().includes('COND') && !k.toUpperCase().includes('CAPACITY')),
        power: keys.find(k => k.toUpperCase().includes('POWER') && !k.toUpperCase().includes('CAPACITY'))
      };
    };

    const wb = XLSX.utils.book_new();
    Object.entries(data).forEach(([site, rows]) => {
      if (Array.isArray(rows) && rows.length > 0) {
        const keys = getExportKeys(rows, site);
        // Only export these columns: Site, DateTime, Feed Flow, Cond Flow, Power Consumption
        const exportRows = rows.map(row => ({
          Site: getDisplayName(site),
          DateTime: row.DateTime || row.Datetime || '',
          ...(keys.feed ? { 'Feed Flow': row[keys.feed] } : {}),
          ...(keys.cond ? { 'Cond Flow': row[keys.cond] } : {}),
          ...(keys.power ? { 'Power Consumption': row[keys.power] } : {})
        }));
        const ws = XLSX.utils.json_to_sheet(exportRows);
        let sheetName = getDisplayName(site).substring(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
    });
    if (wb.SheetNames.length > 0) {
      XLSX.writeFile(wb, 'all_sites_data.xlsx');
    }
  };

  return (
    <Container maxWidth="xl" className="App" sx={{ px: { xs: 1, sm: 2, md: 4 } }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ my: 4, textAlign: 'center', letterSpacing: 2 }}>
        SEDL SITE DATA DASHBOARD
      </Typography>
      
      {loading && <LinearProgress sx={{ mb: 4 }} />}
      
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: { xs: 1, sm: 2 }, borderRadius: 3, boxShadow: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl sx={{ width: { xs: '100%', sm: 300 } }}>
            <InputLabel id="site-select-label">Select Site</InputLabel>
            <Select
              labelId="site-select-label"
              id="site-select"
              value={activeTable}
              label="Select Site"
              onChange={handleTableChange}
              sx={{ 
                textTransform: 'uppercase',
                '& .MuiSelect-select': { 
                  paddingY: '8px',
                  fontSize: '0.9rem'
                }
              }}
            >
              {tables.map(table => (
                <MenuItem 
                  key={table} 
                  value={table}
                  sx={{ textTransform: 'uppercase' }}
                >
                  {getDisplayName(table)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Export all sites button on the right */}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="success"
            onClick={handleExportAllSites}
            disabled={Object.keys(data).length === 0}
            sx={{ minWidth: 180, whiteSpace: 'nowrap' }}
          >
            Export All Sites Data
          </Button>
          {/* Only show the toggle tab in the Fixed Range tab */}
          {activeTab === 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                background: '#fff',
                boxShadow: 1,
                height: 40,
                minWidth: 100,
                overflow: 'hidden'
              }}
            >
              {/* Fixed Range View icon button */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  cursor: 'pointer',
                  background: fixedTabView === 'graph' ? '#e3f2fd' : 'transparent',
                  color: fixedTabView === 'graph' ? '#1976d2' : '#757575',
                  display: 'flex',
                  alignItems: 'center',
                  borderRight: '1px solid #e0e0e0',
                  transition: 'background 0.2s'
                }}
                onClick={() => setFixedTabView('graph')}
                title="Show Fixed Range View"
              >
                <TimelineIcon fontSize="medium" />
              </Box>
              {/* Capacity Comparison icon button */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  cursor: 'pointer',
                  background: fixedTabView === 'capacity' ? '#fff3e0' : 'transparent',
                  color: fixedTabView === 'capacity' ? '#ff9800' : '#757575',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'background 0.2s'
                }}
                onClick={() => setFixedTabView('capacity')}
                title="Show 24h Sum vs Capacity Comparison"
              >
                <BarChartIcon fontSize="medium" />
              </Box>
            </Box>
          )}
        </Stack>
      </Paper>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Real-time View" />
        <Tab label="Fixed Range (7AM-6AM)" />
        <Tab label="Custom Date Range" />
      </Tabs>

      {activeTab === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <DataGraph
            key={`graph-${activeTable}`}
            data={data[activeTable]}
            tableName={activeTable.toUpperCase().replace('-', ' ')}
          />
          <DataTable 
            key={`table-${activeTable}`}
            data={data[activeTable]} 
            tableName={activeTable.toUpperCase().replace('-', ' ')}
            loading={loading}
          />
        </Box>
      ) : activeTab === 1 ? (
        <>
          {fixedTabView === 'capacity' ? (
            <CapacityComparison tableName={activeTable} />
          ) : (
            <FixedRangeView tableName={activeTable} />
          )}
        </>
      ) : (
        <DateRangeView tableName={activeTable} />
      )}
    </Container>
  );
}

export default App;
